import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Eye,
  FloppyDisk,
  Plus,
  QrCode,
  Trash,
  Upload,
} from '@phosphor-icons/react';

import { DashboardLayout } from '../components/DashboardLayout';
import { API, BACKEND_URL, formatApiErrorDetail, useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const SYSTEM_BINDINGS = [
  { key: 'full_name', label: 'System: Vollständiger Name' },
  { key: 'serial_number', label: 'System: Seriennummer' },
  { key: 'created_at_formatted', label: 'System: Erstellungsdatum' },
  { key: 'submission_id', label: 'System: Submission-ID' },
  { key: 'campaign_name', label: 'System: Kampagnenname' },
  { key: 'campaign_date', label: 'System: Kampagnendatum' },
  { key: 'current_date', label: 'System: Heutiges Datum' },
];

const DIMENSION_PRESETS = [
  { value: 'landscape-ticket', label: 'Landscape Ticket', width: 1200, height: 600, dimension: 'landscape' },
  { value: 'portrait-ticket', label: 'Portrait Ticket', width: 600, height: 1000, dimension: 'portrait' },
  { value: 'square-card', label: 'Square Card', width: 800, height: 800, dimension: 'square' },
  { value: 'a4-landscape', label: 'A4 Landscape', width: 1754, height: 1240, dimension: 'a4' },
  { value: 'a4-portrait', label: 'A4 Portrait', width: 1240, height: 1754, dimension: 'a4' },
];

const ELEMENT_LIBRARY = [
  { type: 'text', label: 'Text', description: 'Freies Textfeld für Titel oder Labels' },
  { type: 'image', label: 'Bild', description: 'Bildbereich mit wählbarem Asset' },
  { type: 'logo', label: 'Logo', description: 'Logo-Platz mit Medienauswahl' },
  { type: 'qr_area', label: 'QR Bereich', description: 'QR-Zone für Vorschau und spätere Engine' },
  { type: 'barcode_area', label: 'Barcode Bereich', description: 'Barcode-Zone für spätere PDF-Engine' },
  { type: 'serial_number', label: 'Seriennummer', description: 'Spezialfeld für laufende Nummern' },
  { type: 'free_text', label: 'Freitext', description: 'Statischer Text direkt im Layout' },
  { type: 'dynamic_placeholder', label: 'Dynamischer Platzhalter', description: 'Bindung an Einreichungsdaten' },
];

const TEXTUAL_ELEMENT_TYPES = ['text', 'free_text', 'dynamic_placeholder', 'serial_number'];

const createDefaultTemplate = (user) => ({
  name: 'Neues Template',
  description: '',
  dimension: 'landscape',
  width: 1200,
  height: 600,
  schema_version: 2,
  version: 1,
  coordinate_unit: 'px',
  canvas_origin: 'top-left',
  background_color: '#FFFFFF',
  background_image_url: null,
  background_image_id: null,
  logo_url: null,
  logo_media_id: null,
  logo_position: { x: 50, y: 50, width: 120, height: 80 },
  elements: [],
  placeholders: [],
  metadata: { preview_profile: 'ticket', versioning_ready: true },
  output_formats: ['png'],
  is_base_template: user?.role === 'super_admin',
  is_public: false,
});

const createElement = (type, index) => {
  const basePosition = {
    x: 80,
    y: 80 + (index * 36),
    width: type === 'qr_area' || type === 'barcode_area' ? 140 : (type === 'image' || type === 'logo' ? 180 : 240),
    height: type === 'qr_area' ? 140 : (type === 'image' || type === 'logo' ? 120 : 48),
    rotation: 0,
    z_index: index,
  };
  return {
    id: `element-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    name: ELEMENT_LIBRARY.find((item) => item.type === type)?.label || 'Element',
    binding_key: type === 'dynamic_placeholder' ? 'full_name' : (type === 'serial_number' || type === 'qr_area' ? 'serial_number' : null),
    content: type === 'free_text' || type === 'text' ? 'Textinhalt' : null,
    source_media_id: null,
    source_url: null,
    position: basePosition,
    text_style: {
      font_family: 'IBM Plex Sans',
      font_size: 24,
      font_color: '#09090B',
      font_weight: 'normal',
      text_align: 'left',
      letter_spacing: 0,
      line_height: 1.2,
    },
    config: type === 'qr_area' ? { qr_value_mode: 'serial_number' } : {},
    is_visible: true,
  };
};

export function TemplateDesignerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign_id');
  const { user } = useAuth();
  const dragStateRef = useRef(null);
  const backgroundUploadRef = useRef(null);

  const [template, setTemplate] = useState(null);
  const [campaignContext, setCampaignContext] = useState(null);
  const [campaignFields, setCampaignFields] = useState([]);
  const [departmentId, setDepartmentId] = useState('none');
  const [departments, setDepartments] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [showAddElement, setShowAddElement] = useState(false);
  const [assetPickerTarget, setAssetPickerTarget] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [departmentResult, mediaResult, templateResult, campaignResult] = await Promise.all([
          axios.get(`${API}/departments`, { withCredentials: true }),
          axios.get(`${API}/media`, { params: { kind: 'image', limit: 100 }, withCredentials: true }),
          id ? axios.get(`${API}/templates/${id}`, { withCredentials: true }) : Promise.resolve({ data: createDefaultTemplate(user) }),
          campaignId ? axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }) : Promise.resolve({ data: null }),
        ]);

        setDepartments(departmentResult.data || []);
        setMediaFiles(mediaResult.data.items || []);
        setTemplate(templateResult.data);
        setDepartmentId(templateResult.data.department_id || 'none');
        setSelectedElementId(templateResult.data.elements?.[0]?.id || null);
        setCampaignContext(campaignResult.data || null);
        setCampaignFields(campaignResult.data?.form_fields || []);
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Template konnte nicht geladen werden.');
        navigate('/templates');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate, user]);

  const canvasScale = useMemo(() => {
    if (!template) return 1;
    return Math.min(920 / template.width, 560 / template.height, 1);
  }, [template]);

  const selectedElement = useMemo(() => template?.elements?.find((element) => element.id === selectedElementId) || null, [selectedElementId, template]);
  const availableFieldBindings = useMemo(() => {
    const campaignBindings = campaignFields.map((field) => ({ key: field.id, label: field.label || field.id }));
    const deduped = new Map();
    [...campaignBindings, ...SYSTEM_BINDINGS].forEach((binding) => {
      if (!deduped.has(binding.key)) deduped.set(binding.key, binding);
    });
    return Array.from(deduped.values());
  }, [campaignFields]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!dragStateRef.current || !template) return;
      const { elementId, startX, startY, originX, originY } = dragStateRef.current;
      const deltaX = (event.clientX - startX) / canvasScale;
      const deltaY = (event.clientY - startY) / canvasScale;
      setTemplate((current) => {
        if (!current) return current;
        return {
          ...current,
          elements: current.elements.map((element) => {
            if (element.id !== elementId) return element;
            const maxX = Math.max(current.width - element.position.width, 0);
            const maxY = Math.max(current.height - element.position.height, 0);
            return {
              ...element,
              position: {
                ...element.position,
                x: Math.max(0, Math.min(originX + deltaX, maxX)),
                y: Math.max(0, Math.min(originY + deltaY, maxY)),
              },
            };
          }),
        };
      });
    };

    const handleMouseUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [canvasScale, template]);

  const updateTemplate = (updates) => setTemplate((current) => ({ ...current, ...updates }));

  const updateElement = (elementId, updates) => {
    setTemplate((current) => ({
      ...current,
      elements: current.elements.map((element) => {
        if (element.id !== elementId) return element;
        const nextElement = { ...element, ...updates };
        if (updates.position) nextElement.position = { ...element.position, ...updates.position };
        if (updates.text_style) nextElement.text_style = { ...element.text_style, ...updates.text_style };
        return nextElement;
      }),
    }));
  };

  const removeElement = (elementId) => {
    setTemplate((current) => ({ ...current, elements: current.elements.filter((element) => element.id !== elementId) }));
    setSelectedElementId((current) => (current === elementId ? null : current));
  };

  const addElement = (type) => {
    const nextElement = createElement(type, template.elements.length);
    setTemplate((current) => ({ ...current, elements: [...current.elements, nextElement] }));
    setSelectedElementId(nextElement.id);
    setShowAddElement(false);
  };

  const addBoundField = (binding) => {
    const elementType = binding.key === 'serial_number' ? 'serial_number' : 'dynamic_placeholder';
    const nextElement = createElement(elementType, template.elements.length);
    nextElement.name = binding.label;
    nextElement.binding_key = binding.key;
    setTemplate((current) => ({ ...current, elements: [...current.elements, nextElement] }));
    setSelectedElementId(nextElement.id);
    setShowAddElement(false);
  };

  const handlePresetChange = (presetValue) => {
    const preset = DIMENSION_PRESETS.find((item) => item.value === presetValue);
    if (!preset) return;
    updateTemplate({ width: preset.width, height: preset.height, dimension: preset.dimension });
  };

  const handleSave = async () => {
    if (!template.name.trim()) {
      toast.error('Bitte einen Template-Namen angeben.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...template,
        output_formats: template.output_formats?.length ? template.output_formats : ['png'],
      };
      if (id) {
        await axios.put(`${API}/templates/${id}`, payload, { withCredentials: true });
        toast.success('Template gespeichert.');
      } else {
        const query = departmentId !== 'none' && !template.is_base_template ? `?department_id=${departmentId}` : '';
        const { data } = await axios.post(`${API}/templates${query}`, payload, { withCredentials: true });
        toast.success('Template angelegt.');
        navigate(`/templates/${data.id}/edit`);
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const sampleData = {};
      campaignFields.forEach((field) => {
        const normalized = field.id?.toLowerCase() || '';
        if (normalized.includes('email')) sampleData[field.id] = 'max@example.com';
        else if (normalized.includes('phone') || normalized.includes('telefon')) sampleData[field.id] = '+49 123 456789';
        else if (normalized.includes('first')) sampleData[field.id] = 'Max';
        else if (normalized.includes('last')) sampleData[field.id] = 'Mustermann';
        else if (normalized.includes('name')) sampleData[field.id] = 'Max Mustermann';
        else sampleData[field.id] = `${field.label || field.id} Beispiel`;
      });
      const { data } = await axios.post(`${API}/templates/preview`, {
        template,
        sample_data: {
          full_name: 'Max Mustermann',
          first_name: 'Max',
          last_name: 'Mustermann',
          email: 'max@example.com',
          city: 'Berlin',
          country: 'Deutschland',
          serial_number: 'PREVIEW-001',
          ...sampleData,
        },
        campaign_data: { ...(campaignContext || {}), name: campaignContext?.name || 'Vorschau', enable_qr: true },
      }, { withCredentials: true });
      setPreviewImage(data.data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Vorschau fehlgeschlagen.');
    } finally {
      setPreviewing(false);
    }
  };

  const startDragging = (event, element) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedElementId(element.id);
    dragStateRef.current = {
      elementId: element.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: element.position.x,
      originY: element.position.y,
    };
  };

  const pickAsset = (asset) => {
    if (!assetPickerTarget) return;
    if (assetPickerTarget === 'background') {
      updateTemplate({ background_image_id: asset.id, background_image_url: `${BACKEND_URL}${asset.url}` });
    } else {
      updateElement(assetPickerTarget, { source_media_id: asset.id, source_url: `${BACKEND_URL}${asset.url}` });
    }
    setAssetPickerTarget(null);
  };

  const handleBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'backgrounds');
      formData.append('usage_context', 'template-background');
      formData.append('title', `${template.name || 'template'} background`);
      if (departmentId !== 'none' && !template.is_base_template) formData.append('department_id', departmentId);

      const { data } = await axios.post(`${API}/media/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      updateTemplate({ background_image_id: data.id, background_image_url: `${BACKEND_URL}${data.url}` });
      const refreshed = await axios.get(`${API}/media`, { params: { kind: 'image', limit: 100 }, withCredentials: true });
      setMediaFiles(refreshed.data.items || []);
      toast.success('Hintergrundbild hochgeladen und verknüpft.');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Hintergrund konnte nicht hochgeladen werden.');
    } finally {
      event.target.value = '';
    }
  };

  const getElementLabel = (element) => {
    if (element.type === 'dynamic_placeholder') return `{${element.binding_key || 'field'}}`;
    if (element.type === 'serial_number') return 'SERIAL_NUMBER';
    if (element.type === 'qr_area') return 'QR';
    if (element.type === 'barcode_area') return 'BARCODE';
    if (element.type === 'image' || element.type === 'logo') return element.name;
    return element.content || element.name;
  };

  if (loading || !template) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[420px]" data-testid="template-designer-loading-state">
          <div className="w-8 h-8 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="template-designer-page">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <Button variant="outline" onClick={() => navigate('/templates')} data-testid="template-designer-back-button">
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </Button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Template Designer</p>
              <h1 className="mt-1 text-4xl font-black tracking-tight text-zinc-950">Arbeitsfläche</h1>
              <p className="mt-2 max-w-2xl text-sm text-zinc-600">Sauberes Koordinatensystem, versionierbare Elementstruktur und direkte Medienanbindung für spätere PDF-Engine.</p>
              {campaignContext && <p className="mt-2 text-sm text-[#002FA7]" data-testid="template-designer-campaign-context">Kampagnenkontext: {campaignContext.name}</p>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" onClick={handlePreview} disabled={previewing} data-testid="template-preview-button">
              <Eye className="w-4 h-4" />
              {previewing ? 'Vorschau...' : 'Vorschau'}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90" data-testid="template-save-button">
              <FloppyDisk className="w-4 h-4" />
              {saving ? 'Speichert...' : 'Speichern'}
            </Button>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)_320px] xl:items-start">
          <div className="space-y-4">
            <section className="border border-zinc-200 bg-white p-5 rounded-sm space-y-4" data-testid="template-meta-panel">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Meta</p>
                <Input value={template.name} onChange={(event) => updateTemplate({ name: event.target.value })} className="mt-3" placeholder="Template-Name" data-testid="template-name-input" />
              </div>
              <Textarea value={template.description || ''} onChange={(event) => updateTemplate({ description: event.target.value })} placeholder="Kurzbeschreibung" rows={3} data-testid="template-description-input" />

              {user?.role === 'super_admin' && (
                <label className="flex items-center gap-3 text-sm text-zinc-700" data-testid="template-base-toggle">
                  <input type="checkbox" checked={template.is_base_template} onChange={(event) => updateTemplate({ is_base_template: event.target.checked })} />
                  Als Basistemplate speichern
                </label>
              )}

              <Select value={departmentId} onValueChange={setDepartmentId} disabled={template.is_base_template}>
                <SelectTrigger data-testid="template-department-select"><SelectValue placeholder="Abteilung zuweisen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Abteilung</SelectItem>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={department.id}>{department.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </section>

            <section className="border border-zinc-200 bg-white p-5 rounded-sm space-y-4" data-testid="template-canvas-settings-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Canvas</p>
              <Select onValueChange={handlePresetChange}>
                <SelectTrigger data-testid="template-dimension-preset-select"><SelectValue placeholder="Preset wählen" /></SelectTrigger>
                <SelectContent>
                  {DIMENSION_PRESETS.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value}>{preset.label} ({preset.width}×{preset.height})</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="grid grid-cols-2 gap-3">
                <Input type="number" value={template.width} onChange={(event) => updateTemplate({ width: Number(event.target.value) || 0 })} placeholder="Breite" data-testid="template-width-input" />
                <Input type="number" value={template.height} onChange={(event) => updateTemplate({ height: Number(event.target.value) || 0 })} placeholder="Höhe" data-testid="template-height-input" />
              </div>

              <div className="flex gap-3 items-center">
                <input type="color" value={template.background_color} onChange={(event) => updateTemplate({ background_color: event.target.value })} className="h-10 w-12 rounded-sm border border-zinc-200" data-testid="template-background-color-input" />
                <Input value={template.background_color} onChange={(event) => updateTemplate({ background_color: event.target.value })} data-testid="template-background-color-text-input" />
              </div>
            </section>

            <section className="border border-zinc-200 bg-white p-5 rounded-sm space-y-4" data-testid="template-background-panel">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Hintergrund</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => backgroundUploadRef.current?.click()} data-testid="template-background-upload-button">
                    <Upload className="w-4 h-4" />
                    PNG hochladen
                  </Button>
                  <Button variant="outline" onClick={() => setAssetPickerTarget('background')} data-testid="template-background-picker-button">Bild wählen</Button>
                </div>
              </div>
              <input ref={backgroundUploadRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={handleBackgroundUpload} data-testid="template-background-upload-input" />
              <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600" data-testid="template-background-selection">
                {template.background_image_url ? 'Bild ausgewählt' : 'Kein Hintergrundbild ausgewählt'}
              </div>
              {template.background_image_url && <img src={template.background_image_url} alt="Hintergrund" className="w-full rounded-sm border border-zinc-200" data-testid="template-background-preview-image" />}
              {template.background_image_url && (
                <Button variant="outline" onClick={() => updateTemplate({ background_image_id: null, background_image_url: null })} data-testid="template-background-clear-button">Hintergrund entfernen</Button>
              )}
            </section>

            <section className="border border-zinc-200 bg-white p-5 rounded-sm space-y-4" data-testid="template-elements-panel">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Elemente</p>
                <Button variant="outline" onClick={() => setShowAddElement(true)} data-testid="template-add-element-button">
                  <Plus className="w-4 h-4" />
                  Hinzufügen
                </Button>
              </div>
              <div className="space-y-2 max-h-[280px] overflow-auto">
                {template.elements.length === 0 ? (
                  <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500" data-testid="template-elements-empty-state">Noch keine Elemente vorhanden.</div>
                ) : template.elements.map((element) => (
                  <div
                    key={element.id}
                    className={`flex items-start justify-between gap-3 rounded-sm border px-3 py-3 ${selectedElementId === element.id ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-zinc-200 hover:bg-zinc-50'}`}
                    data-testid={`template-element-list-item-${element.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedElementId(element.id)}
                      className="min-w-0 flex-1 text-left"
                      data-testid={`template-element-select-button-${element.id}`}
                    >
                      <p className="text-sm font-semibold text-zinc-950 truncate">{element.name}</p>
                      <p className="text-xs text-zinc-500">{element.type}</p>
                    </button>
                    <Button variant="ghost" size="icon" onClick={() => removeElement(element.id)} data-testid={`template-element-remove-button-${element.id}`}>
                      <Trash className="w-4 h-4 text-[#E63946]" />
                    </Button>
                  </div>
                ))}
              </div>
            </section>
            <section className="border border-zinc-200 bg-white p-5 rounded-sm space-y-4" data-testid="template-available-fields-panel">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Verfügbare Felder</p>
                <p className="mt-2 text-sm text-zinc-600">Kampagnenfelder werden im UI mit Label angezeigt, intern aber stabil mit `field.id` gebunden.</p>
              </div>
              <div className="space-y-2 max-h-[240px] overflow-auto">
                {availableFieldBindings.map((binding) => (
                  <button key={binding.key} type="button" onClick={() => addBoundField(binding)} className="w-full rounded-sm border border-zinc-200 px-3 py-2 text-left text-sm hover:border-[#002FA7] hover:bg-[#002FA7]/5" data-testid={`template-available-field-${binding.key}`}>
                    <span className="font-medium text-zinc-950">{binding.label}</span>
                    <span className="block text-xs text-zinc-500">{binding.key}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>

          <div className="border border-zinc-200 bg-zinc-100 rounded-sm p-4 overflow-auto" data-testid="template-canvas-shell">
            <div className="min-w-max flex justify-center">
              <div
                className="relative border border-zinc-300 bg-white shadow-sm"
                style={{
                  width: template.width * canvasScale,
                  height: template.height * canvasScale,
                  backgroundColor: template.background_color,
                  backgroundImage: template.background_image_url ? `url(${template.background_image_url})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
                data-testid="template-design-canvas"
              >
                {template.elements.map((element) => (
                  <button
                    type="button"
                    key={element.id}
                    onMouseDown={(event) => startDragging(event, element)}
                    onClick={(event) => { event.stopPropagation(); setSelectedElementId(element.id); }}
                    className={`absolute overflow-hidden border text-left ${selectedElementId === element.id ? 'border-[#002FA7] shadow-[0_0_0_1px_#002FA7]' : 'border-zinc-300 hover:border-zinc-500'}`}
                    style={{
                      left: element.position.x * canvasScale,
                      top: element.position.y * canvasScale,
                      width: Math.max(element.position.width * canvasScale, 40),
                      height: Math.max(element.position.height * canvasScale, 24),
                      background: element.type === 'image' || element.type === 'logo' ? '#ffffff' : 'rgba(255,255,255,0.9)',
                    }}
                    data-testid={`template-canvas-element-${element.id}`}
                  >
                    {(element.type === 'image' || element.type === 'logo') && element.source_url ? (
                      <img src={element.source_url} alt={element.name} className="w-full h-full object-contain" />
                    ) : element.type === 'qr_area' ? (
                      <div className="w-full h-full flex items-center justify-center bg-white text-zinc-500"><QrCode className="w-7 h-7" /></div>
                    ) : (
                      <div
                        className="w-full h-full px-2 py-1 text-zinc-800"
                        style={{
                          fontSize: Math.max((element.text_style?.font_size || 16) * canvasScale, 10),
                          color: element.text_style?.font_color || '#09090B',
                          fontWeight: element.text_style?.font_weight || 'normal',
                          textAlign: element.text_style?.text_align || 'left',
                        }}
                      >
                        {getElementLabel(element)}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <section className="border border-zinc-200 bg-white p-5 rounded-sm space-y-4 min-h-[320px]" data-testid="template-properties-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Elementeigenschaften</p>
              {selectedElement ? (
                <>
                  <Input value={selectedElement.name} onChange={(event) => updateElement(selectedElement.id, { name: event.target.value })} placeholder="Elementname" data-testid="template-element-name-input" />
                  <label className="flex items-center gap-2 text-sm text-zinc-700" data-testid="template-element-visibility-toggle">
                    <input type="checkbox" checked={selectedElement.is_visible !== false} onChange={(event) => updateElement(selectedElement.id, { is_visible: event.target.checked })} />
                    Element sichtbar
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="number" value={selectedElement.position.x} onChange={(event) => updateElement(selectedElement.id, { position: { x: Number(event.target.value) || 0 } })} placeholder="X" data-testid="template-element-x-input" />
                    <Input type="number" value={selectedElement.position.y} onChange={(event) => updateElement(selectedElement.id, { position: { y: Number(event.target.value) || 0 } })} placeholder="Y" data-testid="template-element-y-input" />
                    <Input type="number" value={selectedElement.position.width} onChange={(event) => updateElement(selectedElement.id, { position: { width: Number(event.target.value) || 0 } })} placeholder="Breite" data-testid="template-element-width-input" />
                    <Input type="number" value={selectedElement.position.height} onChange={(event) => updateElement(selectedElement.id, { position: { height: Number(event.target.value) || 0 } })} placeholder="Höhe" data-testid="template-element-height-input" />
                  </div>

                  {TEXTUAL_ELEMENT_TYPES.includes(selectedElement.type) && (
                    <>
                      {(selectedElement.type === 'dynamic_placeholder' || selectedElement.type === 'serial_number') ? (
                        <>
                          <Select value={availableFieldBindings.some((binding) => binding.key === (selectedElement.binding_key || '')) ? selectedElement.binding_key : 'custom'} onValueChange={(value) => {
                            if (value !== 'custom') updateElement(selectedElement.id, { binding_key: value });
                          }}>
                            <SelectTrigger data-testid="template-element-binding-select"><SelectValue placeholder="Bindung" /></SelectTrigger>
                            <SelectContent>
                              {availableFieldBindings.map((binding) => (
                                <SelectItem key={binding.key} value={binding.key}>{binding.label}</SelectItem>
                              ))}
                              <SelectItem value="custom">Eigenes Feld</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input value={selectedElement.binding_key || ''} onChange={(event) => updateElement(selectedElement.id, { binding_key: event.target.value })} placeholder="z. B. donation_amount oder custom_field" data-testid="template-element-binding-input" />
                        </>
                      ) : (
                        <Textarea value={selectedElement.content || ''} onChange={(event) => updateElement(selectedElement.id, { content: event.target.value })} rows={3} placeholder="Textinhalt" data-testid="template-element-content-input" />
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <Input type="number" value={selectedElement.text_style?.font_size || 16} onChange={(event) => updateElement(selectedElement.id, { text_style: { font_size: Number(event.target.value) || 16 } })} placeholder="Fontgröße" data-testid="template-element-font-size-input" />
                        <Input value={selectedElement.text_style?.font_color || '#09090B'} onChange={(event) => updateElement(selectedElement.id, { text_style: { font_color: event.target.value } })} placeholder="#09090B" data-testid="template-element-font-color-input" />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <Select value={selectedElement.text_style?.font_weight || 'normal'} onValueChange={(value) => updateElement(selectedElement.id, { text_style: { font_weight: value } })}>
                          <SelectTrigger data-testid="template-element-font-weight-select"><SelectValue placeholder="Gewicht" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="bold">Fett</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={selectedElement.text_style?.text_align || 'left'} onValueChange={(value) => updateElement(selectedElement.id, { text_style: { text_align: value } })}>
                          <SelectTrigger data-testid="template-element-text-align-select"><SelectValue placeholder="Ausrichtung" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Links</SelectItem>
                            <SelectItem value="center">Zentriert</SelectItem>
                            <SelectItem value="right">Rechts</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {(selectedElement.type === 'image' || selectedElement.type === 'logo') && (
                    <>
                      <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600" data-testid="template-element-asset-preview-state">
                        {selectedElement.source_url ? 'Asset ausgewählt' : 'Noch kein Asset ausgewählt'}
                      </div>
                      <Button variant="outline" onClick={() => setAssetPickerTarget(selectedElement.id)} data-testid="template-element-asset-picker-button">Asset wählen</Button>
                      {selectedElement.source_url && (
                        <Button variant="outline" onClick={() => updateElement(selectedElement.id, { source_media_id: null, source_url: null })} data-testid="template-element-asset-clear-button">Asset entfernen</Button>
                      )}
                    </>
                  )}

                  {(selectedElement.type === 'qr_area' || selectedElement.type === 'barcode_area') && (
                    <>
                      {selectedElement.type === 'qr_area' && (
                        <Select value={selectedElement.config?.qr_value_mode || 'serial_number'} onValueChange={(value) => updateElement(selectedElement.id, { config: { ...(selectedElement.config || {}), qr_value_mode: value } })}>
                          <SelectTrigger data-testid="template-element-qr-mode-select"><SelectValue placeholder="QR-Inhalt" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="serial_number">Immer Seriennummer</SelectItem>
                            <SelectItem value="binding_key">Gebundenes Feld verwenden</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <Select value={availableFieldBindings.some((binding) => binding.key === (selectedElement.binding_key || '')) ? selectedElement.binding_key : 'custom'} onValueChange={(value) => {
                        if (value !== 'custom') updateElement(selectedElement.id, { binding_key: value });
                      }}>
                        <SelectTrigger data-testid="template-element-code-binding-select"><SelectValue placeholder="Code-Bindung" /></SelectTrigger>
                        <SelectContent>
                          {availableFieldBindings.map((binding) => (
                            <SelectItem key={binding.key} value={binding.key}>{binding.label}</SelectItem>
                          ))}
                          <SelectItem value="custom">Eigenes Feld</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input value={selectedElement.binding_key || ''} onChange={(event) => updateElement(selectedElement.id, { binding_key: event.target.value })} placeholder="z. B. serial_number oder submission_id" data-testid="template-element-code-binding-input" />
                    </>
                  )}
                </>
              ) : (
                <p className="text-sm text-zinc-500" data-testid="template-properties-empty-state">Wähle ein Element auf der Fläche oder links in der Liste aus.</p>
              )}
            </section>

            <section className="border border-zinc-200 bg-white p-5 rounded-sm" data-testid="template-architecture-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Architektur</p>
              <div className="mt-3 space-y-2 text-sm text-zinc-600">
                <p data-testid="template-schema-version-value">Schema-Version: {template.schema_version}</p>
                <p data-testid="template-coordinate-unit-value">Koordinaten: {template.coordinate_unit} · Ursprung {template.canvas_origin}</p>
                <p data-testid="template-element-count-value">Elemente: {template.elements.length}</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Dialog open={showAddElement} onOpenChange={setShowAddElement}>
        <DialogContent className="max-w-2xl rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]" data-testid="template-add-element-dialog">
          <DialogHeader>
            <DialogTitle data-testid="template-add-element-dialog-title">Element hinzufügen</DialogTitle>
            <DialogDescription data-testid="template-add-element-dialog-description">Wähle den Elementtyp für das Layout. Die Architektur bleibt später für weitere Varianten erweiterbar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
            {ELEMENT_LIBRARY.map((item) => (
              <button
                key={item.type}
                type="button"
                onClick={() => addElement(item.type)}
                className="rounded-sm border border-zinc-200 p-4 text-left hover:border-[#002FA7] hover:bg-[#002FA7]/5"
                data-testid={`template-element-option-${item.type}`}
              >
                <p className="font-semibold text-zinc-950">{item.label}</p>
                <p className="mt-1 text-sm text-zinc-600">{item.description}</p>
              </button>
            ))}
            </div>
            {availableFieldBindings.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">Direkt ein Kampagnenfeld ins Ticket setzen</p>
                <div className="grid gap-2 sm:grid-cols-2 max-h-[220px] overflow-auto">
                  {availableFieldBindings.map((binding) => (
                    <button key={binding.key} type="button" onClick={() => addBoundField(binding)} className="rounded-sm border border-zinc-200 p-3 text-left hover:border-[#002FA7] hover:bg-[#002FA7]/5" data-testid={`template-dialog-field-option-${binding.key}`}>
                      <span className="text-sm font-medium text-zinc-950">{binding.label}</span>
                      <span className="block text-xs text-zinc-500">{binding.key}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assetPickerTarget)} onOpenChange={(open) => !open && setAssetPickerTarget(null)}>
        <DialogContent className="max-w-4xl rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]" data-testid="template-asset-picker-dialog">
          <DialogHeader>
            <DialogTitle data-testid="template-asset-picker-title">Bild aus Media Library wählen</DialogTitle>
            <DialogDescription data-testid="template-asset-picker-description">Verfügbare Bilddateien können als Hintergrund, Bild- oder Logo-Element verwendet werden.</DialogDescription>
          </DialogHeader>
          {mediaFiles.length === 0 ? (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-500" data-testid="template-asset-picker-empty-state">Keine Bilddateien verfügbar. Bitte zuerst in der Media Library hochladen.</div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-h-[60vh] overflow-auto">
              {mediaFiles.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => pickAsset(asset)}
                  className="rounded-sm border border-zinc-200 bg-white p-3 text-left hover:border-[#002FA7] hover:bg-[#002FA7]/5"
                  data-testid={`template-asset-option-${asset.id}`}
                >
                  <img src={`${BACKEND_URL}${asset.url}`} alt={asset.original_filename} className="h-36 w-full rounded-sm object-cover border border-zinc-200" />
                  <p className="mt-3 text-sm font-semibold text-zinc-950 truncate">{asset.original_filename}</p>
                  <p className="text-xs text-zinc-500">{asset.folder}</p>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-5xl rounded-sm border-2 border-zinc-950 shadow-[8px_8px_0px_0px_rgba(9,9,11,1)]" data-testid="template-preview-dialog">
          <DialogHeader>
            <DialogTitle data-testid="template-preview-dialog-title">Template-Vorschau</DialogTitle>
            <DialogDescription data-testid="template-preview-dialog-description">Serverseitig gerenderte Vorschau mit Beispieldaten für Positionierung, Text und QR-Bereich.</DialogDescription>
          </DialogHeader>
          {previewImage ? (
            <img src={previewImage} alt="Template Vorschau" className="w-full rounded-sm border border-zinc-200" data-testid="template-preview-image" />
          ) : (
            <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-6 text-sm text-zinc-500">Noch keine Vorschau vorhanden.</div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
